import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Space, 
  Tag, 
  Typography, 
  Popconfirm,
  App,
  Upload,
  Modal,
  Input,
  Select,
  Row,
  Col
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, UploadOutlined, BgColorsOutlined, SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, CloudServerOutlined, CopyOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { websiteApi } from '../services/api';
import { getApiBaseUrl } from '../config/api';
import { Website } from '../types/website';

const { Title } = Typography;
const { Option } = Select;

interface FilterState {
  sslStatus?: boolean;
  hasDirLinks?: boolean;
  isGenerated?: boolean;
  isActive?: boolean;
  cloudflare_domain_onboarded?: boolean;
  isIndexed?: boolean;
  hasGaKey?: boolean;
  search?: string;
}

const Websites: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  
  // Load filters from localStorage first (needed for searchText)
  const savedFilters = (() => {
    const saved = localStorage.getItem('websites_filters');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  })();

  // Load searchText from localStorage on mount, prioritize filters.search if exists
  const [searchText, setSearchText] = useState(() => {
    return savedFilters.search || localStorage.getItem('websites_searchText') || '';
  });
  
  // Load currentPage from localStorage on mount
  const [currentPage, setCurrentPage] = useState(() => {
    const savedCurrentPage = localStorage.getItem('websites_currentPage');
    return savedCurrentPage ? parseInt(savedCurrentPage, 10) : 1;
  });
  
  // Load pageSize from localStorage on mount
  const [pageSize, setPageSize] = useState(() => {
    const savedPageSize = localStorage.getItem('websites_pageSize');
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  });
  
  // Load filters from localStorage on mount (normalize boolean fields saved as strings)
  const [filters, setFilters] = useState<FilterState>(() => {
    const normalized = { ...savedFilters };
    if (normalized.hasGaKey === 'true' || normalized.hasGaKey === true) {
      normalized.hasGaKey = true;
    } else if (normalized.hasGaKey === 'false' || normalized.hasGaKey === false) {
      normalized.hasGaKey = false;
    } else {
      delete normalized.hasGaKey;
    }
    return normalized;
  });

  // Save pageSize to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('websites_pageSize', pageSize.toString());
  }, [pageSize]);

  // Save currentPage to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('websites_currentPage', currentPage.toString());
  }, [currentPage]);

  // Save searchText to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('websites_searchText', searchText);
  }, [searchText]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('websites_filters', JSON.stringify(filters));
  }, [filters]);

  const { data: websitesData, isLoading, isFetching, refetch } = useQuery(
    ['websites', currentPage, pageSize, filters],
    () => websiteApi.getAll({
      page: currentPage,
      limit: pageSize,
      ...filters,
      hasGaKey: filters.hasGaKey === true ? true : filters.hasGaKey === false ? false : undefined,
    }),
    {
      onSuccess: (data) => {
        console.log('Websites data loaded:', data);
      },
      select: (data) => {
        // Transform _id to id for compatibility
        return {
          ...data,
          data: data.data.map(website => ({
            ...website,
            id: website.id || (website as any)._id
          }))
        };
      }
    }
  );

  const websites = websitesData?.data || [];
  const total = websitesData?.total || 0;

  const deleteMutation = useMutation(websiteApi.delete, {
    onSuccess: () => {
      console.log('Website deleted successfully');
      message.success('Website deleted successfully!');
      queryClient.invalidateQueries('websites');
    },
    onError: (error: any) => {
      console.error('Delete error:', error);
      console.error('Error response:', error.response);
      message.error(error.response?.data?.message || error.message || 'Failed to delete website');
    },
  });

  // Import websites mutation
  const importWebsitesMutation = useMutation(
    (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const importUrl = `${getApiBaseUrl()}/websites/import`;
      
      return fetch(importUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      }).then(res => res.json());
    },
    {
      onSuccess: (result) => {
        if (result.errors && result.errors.length > 0) {
          message.warning(`Imported ${result.success} websites with ${result.errors.length} errors`);
          console.log('Import errors:', result.errors);
        } else {
          message.success(`Successfully imported ${result.success} websites!`);
        }
        queryClient.invalidateQueries('websites');
        setIsImportModalVisible(false);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to import websites');
      },
    }
  );

  const handleCreate = () => {
    navigate('/websites/new');
  };

  const handleImport = (file: File) => {
    importWebsitesMutation.mutate(file);
    return false; // Prevent default upload
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        domain: 'mytechblog.local',
        description: 'A technology blog about web development',
        theme: 'modern'
      },
      {
        domain: 'mystore.local',
        description: 'Online store for selling products',
        theme: 'elegant'
      },
      {
        domain: 'portfolio.local',
        description: 'Personal portfolio website',
        theme: 'minimal'
      }
    ];

    const csvContent = [
      // Header - only domain is required, others are optional
      'domain,description,theme,isActive,type',
      // Data rows
      ...sampleData.map(row => 
        `"${row.domain}","${row.description || ''}","${row.theme || 'modern'}","true","static"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'websites-sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = (website: Website) => {
    navigate(`/websites/${website.id}/edit`);
  };

  const handleDelete = (id: string) => {
    console.log('Attempting to delete website with ID:', id);
    console.log('ID type:', typeof id);
    console.log('ID value:', id);
    
    if (!id || id === 'undefined') {
      message.error('Invalid website ID');
      return;
    }
    
    deleteMutation.mutate(id);
  };

  const checkWebsiteGenerated = (website: Website) => {
    // Check if index.html exists for this website
    // This will be handled by the backend API
    return website.isGenerated || false;
  };

  const renderGeneratedIcon = (website: Website) => {
    const isGenerated = checkWebsiteGenerated(website);

    return isGenerated ? (
      <CheckCircleOutlined
        style={{ fontSize: '16px', color: '#52c41a' }}
        title="Website generated successfully"
      />
    ) : (
      <CloseCircleOutlined
        style={{ fontSize: '16px', color: '#ff4d4f' }}
        title="Website not generated yet"
      />
    );
  };

  const columns = [
    {
      title: 'Basic Info',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left' as const,
      render: (text: string, record: Website) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flexShrink: 0, paddingTop: 2 }}>
            {renderGeneratedIcon(record)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.domain}</div>
          {record.cloudflare_domain_onboarded && record.cloudflare_zone_id ? (
            <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span 
                style={{ 
                  fontSize: '11px', 
                  color: '#1890ff',
                  fontFamily: 'monospace'
                }}
              >
                {record.cloudflare_zone_id}
              </span>
              <CopyOutlined 
                style={{ 
                  fontSize: '18px', 
                  color: '#1890ff',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(record.cloudflare_zone_id || '');
                  message.success('Zone ID copied to clipboard!');
                }}
                title="Copy Zone ID"
              />
            </div>
          ) : (
            record.cloudflare_domain_onboarded && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                Zone ID: N/A
              </div>
            )
          )}
          {record.theme && (
            <div style={{ marginTop: '4px' }}>
              <Tag 
                color="purple" 
                icon={<BgColorsOutlined />}
                style={{
                  fontSize: '11px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-primary)'
                }}
              >
                {record.theme}
              </Tag>
            </div>
          )}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string, record: Website) => {
        if (!status) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        // Map status to color
        const statusColors: Record<string, string> = {
          'active': 'green',
          'inactive': 'orange',
          'building': 'blue',
          'error': 'red',
          'redirected': 'purple',
        };
        const color = statusColors[status.toLowerCase()] || 'default';
        return (
          <Tag color={color}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'GA Tag Key',
      dataIndex: 'ga_tag_key',
      key: 'ga_tag_key',
      width: 140,
      align: 'center' as const,
      render: (gaTagKey: string | undefined) => (
        gaTagKey ? (
          <Tag color="green">{gaTagKey}</Tag>
        ) : (
          <Tag color="default">No</Tag>
        )
      ),
    },
    {
      title: 'SSL',
      key: 'ssl',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: Website) => (
        <Tag 
          color={record.sslStatus?.hasSSL ? 'green' : 'red'}
          icon={record.sslStatus?.hasSSL ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {record.sslStatus?.hasSSL ? 'SSL' : 'No SSL'}
        </Tag>
      ),
    },
    {
      title: 'Cloudflare',
      key: 'cloudflare',
      width: 110,
      align: 'center' as const,
      render: (_: any, record: Website) => (
        <Tag 
          color={record.cloudflare_domain_onboarded ? 'green' : 'default'}
          icon={<CloudServerOutlined />}
        >
          {record.cloudflare_domain_onboarded ? 'Onboarded' : 'Not Onboarded'}
        </Tag>
      ),
    },
    {
      title: 'CF Status',
      key: 'cloudflare_status',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Website) => {
        if (!record.cloudflare_domain_onboarded) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        if (!record.cloudflare_domain_status) {
          return <span style={{ color: '#999' }}>Unknown</span>;
        }
        // Map Cloudflare status to color
        const statusColors: Record<string, string> = {
          'active': 'green',
          'pending': 'orange',
          'initializing': 'blue',
          'moved': 'purple',
          'deleted': 'red',
        };
        const color = statusColors[record.cloudflare_domain_status.toLowerCase()] || 'default';
        return (
          <Tag color={color}>
            {record.cloudflare_domain_status}
          </Tag>
        );
      },
    },
    {
      title: 'Indexed',
      key: 'isIndexed',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Website) => (
        <Tag 
          color={record.isIndexed ? 'green' : 'red'}
          icon={record.isIndexed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {record.isIndexed ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 90,
      align: 'center' as const,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_: any, record: Website) => {
        console.log('Rendering actions for website:', record);
        return (
          <Space>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                console.log('Navigate to website ID:', record.id);
                navigate(`/websites/${record.id}`);
              }}
            />
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
            <Popconfirm
              title="Are you sure you want to delete this website?"
              onConfirm={() => {
                console.log('Delete website ID:', record.id);
                handleDelete(record.id);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      // Remove undefined values
      Object.keys(newFilters).forEach(k => {
        if (newFilters[k as keyof FilterState] === undefined || newFilters[k as keyof FilterState] === '') {
          delete newFilters[k as keyof FilterState];
        }
      });
      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchText('');
    setCurrentPage(1);
    // Clear localStorage for filters
    localStorage.removeItem('websites_filters');
    localStorage.removeItem('websites_searchText');
    localStorage.removeItem('websites_currentPage');
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    handleFilterChange('search', value || undefined);
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    // setPageSize will trigger useEffect to save to localStorage
    setPageSize(pagination.pageSize);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Websites</Title>
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setIsImportModalVisible(true)}
          >
            Import Websites
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Website
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                SSL Status:
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.sslStatus !== undefined ? filters.sslStatus.toString() : undefined}
                onChange={(value) => handleFilterChange('sslStatus', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Already Dir Links:
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.hasDirLinks !== undefined ? filters.hasDirLinks.toString() : undefined}
                onChange={(value) => handleFilterChange('hasDirLinks', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Generated:
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.isGenerated !== undefined ? filters.isGenerated.toString() : undefined}
                onChange={(value) => handleFilterChange('isGenerated', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Active:
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.isActive !== undefined ? filters.isActive.toString() : undefined}
                onChange={(value) => handleFilterChange('isActive', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Cloudflare:
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.cloudflare_domain_onboarded !== undefined ? filters.cloudflare_domain_onboarded.toString() : undefined}
                onChange={(value) => handleFilterChange('cloudflare_domain_onboarded', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Onboarded</Option>
                <Option value="false">Not Onboarded</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Google Indexed?
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.isIndexed !== undefined ? filters.isIndexed.toString() : undefined}
                onChange={(value) => handleFilterChange('isIndexed', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Add GA Key:
              </Typography.Text>
              <Select
                placeholder="All"
                style={{ width: '100%' }}
                value={filters.hasGaKey !== undefined ? filters.hasGaKey.toString() : undefined}
                onChange={(value) => handleFilterChange('hasGaKey', value === 'true' ? true : value === 'false' ? false : undefined)}
                allowClear
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                Search:
              </Typography.Text>
              <Input
                placeholder="Search by name, domain, or URL..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                onClick={() => refetch()}
                icon={<ReloadOutlined />}
                style={{
                  width: '100%',
                }}
                loading={isFetching}
              >
                Reload
              </Button>
              <Button
                onClick={handleClearFilters}
                icon={<ReloadOutlined />}
                style={{
                  width: '100%',
                  background: '#ff7875',
                  borderColor: '#ff7875',
                  color: '#fff'
                }}
                loading={isFetching}
              >
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={websites}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} websites`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Import Websites Modal */}
      <Modal
        title="Import Websites"
        open={isImportModalVisible}
        onCancel={() => setIsImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Upload a CSV file to import multiple websites at once.</p>
          <p><strong>Required fields:</strong> domain</p>
          <p><strong>Optional fields:</strong> description, theme, isActive, type, name, url, outputDir</p>
          <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f0f0f0', borderRadius: 4, fontSize: '12px' }}>
            <p style={{ margin: 0, marginBottom: 8 }}><strong>Auto-generated fields (if not provided):</strong></p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>name</strong> = domain</li>
              <li><strong>type</strong> = 'static'</li>
              <li><strong>url</strong> = 'https://' + domain</li>
              <li><strong>outputDir</strong> = static_sites_path (from settings) + '/' + domain</li>
              <li><strong>theme</strong> = Round-robin through 5 themes (modern, dark, colorful, professional, creative)</li>
            </ul>
          </div>
        </div>
        
        <Upload.Dragger
          accept=".csv"
          beforeUpload={handleImport}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for CSV files only
          </p>
        </Upload.Dragger>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button 
            type="link" 
            onClick={handleDownloadSample}
            style={{ padding: 0 }}
          >
            📥 Download sample CSV file
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Websites;
